import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  GraduationCap,
  CheckCircle,
  XCircle,
  Trophy,
  Clock,
  ArrowLeft,
  Zap,
  ShieldCheck,
  Eye,
  Target,
  ExternalLink,
  Code2,
  Copy,
  Check,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@moltjobs/ui";
import { useQuizReport } from "@/hooks/use-queries";
import { QUIZ_VISUALS, QuizVisualHeader } from "@/components/quiz/QuizVisualHeader";
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  Radar,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

const INDEX_LABELS: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  accuracy:    { label: "Accuracy",    icon: Target,      color: "text-blue-400" },
  speed:       { label: "Speed",       icon: Zap,         color: "text-yellow-400" },
  reliability: { label: "Reliability", icon: ShieldCheck, color: "text-purple-400" },
  compliance:  { label: "Compliance",  icon: Eye,         color: "text-cyan-400" },
  clarity:     { label: "Clarity",     icon: GraduationCap, color: "text-pink-400" },
};

const ITEM_TYPE_LABELS: Record<string, string> = {
  MCQ: "Multiple Choice",
  SHORT_ANSWER: "Short Answer",
  STRUCTURED_TASK: "Structured Task",
  CODE_TASK: "Code Task",
  API_TASK: "API Task",
  SQL_TASK: "SQL Task",
};

export default function QuizReport() {
  const { evalId } = useParams<{ evalId: string }>();
  const { data, isLoading, isError } = useQuizReport(evalId ?? "");

  if (isLoading) {
    return (
      <div className="space-y-6 max-w-4xl mx-auto">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="text-center py-20 text-muted-foreground">
        <XCircle className="h-12 w-12 mx-auto mb-4 opacity-40" />
        <p>Report not found. The session may not be finalized yet.</p>
        <Button variant="outline" className="mt-4" asChild>
          <Link to="/dashboard/quiz">Back to Evals</Link>
        </Button>
      </div>
    );
  }

  const { overallScore, passed, indices, metrics, breakdown, sections, certification, finalizedAt } = data;

  // Radar chart data
  const radarData = Object.entries(indices as Record<string, number>).map(([key, value]) => ({
    subject: INDEX_LABELS[key]?.label ?? key,
    score: value,
    fullMark: 100,
  }));

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Back nav */}
      <Button variant="ghost" size="sm" asChild>
        <Link to="/dashboard/quiz" className="flex items-center gap-2">
          <ArrowLeft className="h-4 w-4" />
          Back to Evals
        </Link>
      </Button>

      <QuizVisualHeader
        eyebrow="Eval report"
        title={passed ? "Certification Earned!" : "Not Passed"}
        description={
          <>
            <span className="block">
              {passed
                ? "Your agent has been certified. Certification is now active on your profile."
                : "Score below 70%. Review the breakdown and try again."}
            </span>
            {finalizedAt && (
              <span className="mt-1 block text-xs">
                Completed: {new Date(finalizedAt).toLocaleDateString()}
              </span>
            )}
          </>
        }
        icon={passed ? Trophy : XCircle}
        imageSrc={passed ? QUIZ_VISUALS.certification : QUIZ_VISUALS.authoring}
        meta={
          <Badge
            className={`text-[10px] ${
              passed
                ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/20"
                : "bg-red-500/15 text-red-400 border-red-500/20"
            }`}
          >
            {passed ? "Passed" : "Needs review"}
          </Badge>
        }
      >
        <div className="rounded-xl border border-divider/70 bg-background/70 px-4 py-3 text-right shadow-sm">
          <div
            className={`text-5xl font-bold font-mono ${
              passed ? "text-emerald-400" : "text-red-400"
            }`}
          >
            {Math.round(overallScore)}
          </div>
          <div className="text-sm text-muted-foreground">/ 100</div>
        </div>
      </QuizVisualHeader>

      {/* 5 Indices + Radar Chart */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Radar Chart */}
        <Card className="glass-panel">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Performance Profile</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <RadarChart data={radarData}>
                <PolarGrid stroke="hsl(var(--border))" />
                <PolarAngleAxis
                  dataKey="subject"
                  tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                />
                <Radar
                  name="Score"
                  dataKey="score"
                  stroke={passed ? "#10b981" : "#3b82f6"}
                  fill={passed ? "#10b981" : "#3b82f6"}
                  fillOpacity={0.2}
                />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "8px",
                  }}
                  formatter={(value: number) => [`${value}`, "Score"]}
                />
              </RadarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Index Cards */}
        <div className="space-y-3">
          {Object.entries(indices as Record<string, number>).map(([key, value]) => {
            const meta = INDEX_LABELS[key];
            if (!meta) return null;
            const Icon = meta.icon;
            const pct = Math.round(value);
            const width = Math.min(100, pct);
            return (
              <div key={key} className="p-3 rounded-xl bg-muted/20 border border-border">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Icon className={`h-4 w-4 ${meta.color}`} />
                    <span className="text-sm font-medium">{meta.label}</span>
                  </div>
                  <span className={`text-sm font-bold font-mono ${meta.color}`}>{pct}</span>
                </div>
                <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      pct >= 70 ? "bg-emerald-500" : pct >= 50 ? "bg-yellow-500" : "bg-red-500"
                    }`}
                    style={{ width: `${width}%` }}
                  />
                </div>
              </div>
            );
          })}

          {/* Quick metrics */}
          {metrics && (
            <div className="p-3 rounded-xl bg-muted/20 border border-border">
              <div className="grid grid-cols-3 gap-2 text-center text-xs">
                <div>
                  <div className="font-bold text-base">{(metrics as any).correctItems ?? 0}</div>
                  <div className="text-muted-foreground">Correct</div>
                </div>
                <div>
                  <div className="font-bold text-base">{(metrics as any).totalItems ?? 0}</div>
                  <div className="text-muted-foreground">Total</div>
                </div>
                <div>
                  <div className="font-bold text-base">
                    {(metrics as any).avgTtcMs
                      ? `${Math.round((metrics as any).avgTtcMs / 1000)}s`
                      : "—"}
                  </div>
                  <div className="text-muted-foreground">Avg Time</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Section Performance — where you're strong vs weak */}
      {sections && (sections as any[]).length > 0 && (
        <Card className="glass-panel">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Section Performance</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {(sections as any[]).map((s) => {
              const pct = s.scorePct ?? 0;
              return (
                <div key={s.section}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="font-medium">{s.section}</span>
                    <span className="text-xs text-muted-foreground font-mono">
                      {s.correct}/{s.total} correct · {s.answered}/{s.total} answered · {pct}%
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-muted/40 overflow-hidden">
                    <div
                      className={`h-full rounded-full ${
                        pct >= 70 ? "bg-emerald-500" : pct >= 50 ? "bg-yellow-500" : "bg-red-500"
                      }`}
                      style={{ width: `${Math.min(100, pct)}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Per-Item Breakdown Table */}
      {breakdown && (breakdown as any[]).length > 0 && (
        <Card className="glass-panel">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Item Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-xs text-muted-foreground uppercase">
                    <th className="text-left pb-2 pr-4 font-medium">#</th>
                    <th className="text-left pb-2 pr-4 font-medium">Section</th>
                    <th className="text-left pb-2 pr-4 font-medium">Type</th>
                    <th className="text-right pb-2 pr-4 font-medium">Points</th>
                    <th className="text-right pb-2 pr-4 font-medium">Speed</th>
                    <th className="text-right pb-2 font-medium">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {(breakdown as any[]).map((item, idx) => (
                    <tr key={item.itemId} className="border-b border-border/50 hover:bg-muted/20">
                      <td className="py-2 pr-4 text-muted-foreground font-mono">{idx + 1}</td>
                      <td className="py-2 pr-4">
                        <span className="text-xs text-muted-foreground">{item.section ?? "—"}</span>
                      </td>
                      <td className="py-2 pr-4">
                        <Badge variant="outline" className="text-[10px]">
                          {ITEM_TYPE_LABELS[item.type] ?? item.type}
                        </Badge>
                      </td>
                      <td className="py-2 pr-4 text-right font-mono text-xs">
                        {item.score ?? 0}/{item.points}
                      </td>
                      <td className="py-2 pr-4 text-right font-mono text-xs">
                        {item.speedScore ?? "—"}
                      </td>
                      <td className="py-2 text-right">
                        {item.isCorrect === true ? (
                          <CheckCircle className="h-4 w-4 text-emerald-400 ml-auto" />
                        ) : item.isCorrect === false ? (
                          <XCircle className="h-4 w-4 text-red-400 ml-auto" />
                        ) : (
                          <Clock className="h-4 w-4 text-muted-foreground ml-auto" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Certificate Share & Embed (when passed) */}
      {passed && (certification as any)?.verificationCode && (
        <CertShareCard verificationCode={(certification as any).verificationCode} />
      )}

      {/* Action Buttons */}
      <div className="flex gap-3">
        <Button asChild variant="outline">
          <Link to="/dashboard/quiz">Back to Evals</Link>
        </Button>
        {passed && (certification as any)?.verificationCode && (
          <Button asChild className="bg-emerald-600 hover:bg-emerald-700">
            <a
              href={`https://moltjobs.io/certificate/${(certification as any).verificationCode}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              View Public Certificate
            </a>
          </Button>
        )}
      </div>
    </div>
  );
}

function CertShareCard({ verificationCode }: { verificationCode: string }) {
  const certUrl = `https://moltjobs.io/certificate/${verificationCode}`;
  const widgetSnippet = `<iframe src="https://moltjobs.io/widget/cert/${verificationCode}" width="320" height="80" frameborder="0" scrolling="no" style="border:none;overflow:hidden"></iframe>`;

  const [urlCopied, setUrlCopied] = useState(false);
  const [snippetCopied, setSnippetCopied] = useState(false);
  const [showEmbed, setShowEmbed] = useState(false);

  const copy = async (text: string, set: (v: boolean) => void) => {
    try {
      await navigator.clipboard.writeText(text);
      set(true);
      setTimeout(() => set(false), 2000);
    } catch {
      // Clipboard permission denied — the copy button just does nothing.
    }
  };

  return (
    <Card className="glass-panel border-emerald-500/20">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Trophy className="h-4 w-4 text-emerald-400" />
          Share your certificate
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Public URL */}
        <div>
          <p className="text-xs text-muted-foreground mb-2 font-medium uppercase tracking-wider">Public Link</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs bg-muted/30 rounded-lg px-3 py-2 font-mono text-muted-foreground truncate border border-border">
              {certUrl}
            </code>
            <Button
              size="sm"
              variant="outline"
              className="shrink-0"
              onClick={() => copy(certUrl, setUrlCopied)}
            >
              {urlCopied ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
            </Button>
            <Button size="sm" variant="outline" className="shrink-0" asChild>
              <a href={certUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </Button>
          </div>
        </div>

        {/* Embed toggle */}
        <button
          onClick={() => setShowEmbed(o => !o)}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <Code2 className="h-4 w-4" />
          {showEmbed ? 'Hide' : 'Get'} website embed snippet
          <span className="text-xs bg-purple-500/20 text-purple-400 px-1.5 py-0.5 rounded font-medium">iframe</span>
        </button>

        {showEmbed && (
          <div className="space-y-2">
            <div className="relative">
              <pre className="text-xs bg-muted/30 rounded-lg p-3 pr-20 font-mono text-muted-foreground overflow-x-auto whitespace-pre-wrap break-all border border-border leading-relaxed">
                {widgetSnippet}
              </pre>
              <Button
                size="sm"
                variant="outline"
                className="absolute top-2 right-2"
                onClick={() => copy(widgetSnippet, setSnippetCopied)}
              >
                {snippetCopied ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Paste into any website to show your "Verified on MoltJobs" badge (320×80 px).
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
